// Path: src/components/aols/olsme/olsmeSub.tsx
// Updated to improve mobile responsiveness, add search/filter, CTA, and SEO
// Optimized for macOS 11 and mobile (iPhone 12, 390x844)
// 06/04/25 05:01 PM +07

import React, { useState } from "react";
import { Typography, Table, Input, Button, Card, Row, Col } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { OLSME_SUBSCRIPTION_DATA, SubscriptionAgeTier } from "../../aols/olsme/constants/subscriptionData";
import "./olsme.css";

const { Title, Paragraph } = Typography;
const { Search } = Input;

const SubscriptionPageWrapper = styled.div`
  padding: 40px;
  max-width: 1200px;
  margin: 0 auto;
  background: #000000;
  color: #fff;
`;
const SubscriptionTableStyled = styled(Table)`
  .ant-table {
    background: #1a1a1a;
    color: #fff;
  }
  .ant-table-thead > tr > th {
    background: #333;
    color: #ffcc00;
    font-weight: bold;
    text-align: center;
  }
  .ant-table-tbody > tr > td {
    background: #2a2a2a;
    color: #fff;
    text-align: center;
    border-bottom: 1px solid #ffaa00;
  }
  .ant-table-tbody > tr:hover > td {
    background: #333;
  }
  @media (max-width: 768px) {
    display: none;
  }
`;
const SubscriptionCardStyled = styled(Card)`
  background: linear-gradient(135deg, #1a1a1a, #2a2a2a);
  border: 1px solid #ffaa00;
  border-radius: 12px;
  color: #fff;
  margin-bottom: 16px;
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
  .ant-card-body {
    padding: 16px;
  }
`;
const CardTitle = styled(Title)`
  color: #ffcc00 !important;
  font-size: 18px !important;
  margin-bottom: 8px !important;
`;
const CardText = styled(Paragraph)`
  color: #fff;
  font-size: 14px;
  margin-bottom: 4px !important;
`;
const SearchWrapper = styled.div`
  margin-bottom: 20px;
  max-width: 300px;
  .ant-input {
    background: #333;
    color: #fff;
    border: 1px solid #ffaa00;
  }
`;
const CTASectionStyled = styled.section`
  background: #1a1a1a;
  padding: 40px 20px;
  text-align: center;
  border-top: 2px solid #ffaa00;
  border-bottom: 2px solid #ffaa00;
  margin-top: 40px;
`;

const OlsmeSub: React.FC = () => {
  const [searchText, setSearchText] = useState("");

  const filteredData = OLSME_SUBSCRIPTION_DATA.filter((tier) =>
    tier.ageDisplay.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    { title: "Age", dataIndex: "ageDisplay", key: "ageDisplay" },
    {
      title: "1,000 Pop.",
      dataIndex: "1000",
      key: "1000",
      render: (pricing: SubscriptionAgeTier['1000']) =>
        `${pricing.regular.toFixed(2)}${pricing.promo ? ` (${pricing.promo.toFixed(2)} promo)` : ""}`,
    },
    {
      title: "10,000 Pop.",
      dataIndex: "10000",
      key: "10000",
      render: (pricing: SubscriptionAgeTier['10000']) =>
        `${pricing.regular.toFixed(2)}${pricing.promo ? ` (${pricing.promo.toFixed(2)} promo)` : ""}`,
    },
    {
      title: "100,000 Pop.",
      dataIndex: "100000",
      key: "100000",
      render: (pricing: SubscriptionAgeTier['100000']) =>
        `${pricing.regular.toFixed(2)}${pricing.promo ? ` (${pricing.promo.toFixed(2)} promo)` : ""}`,
    },
    {
      title: "1,000,000 Pop.",
      dataIndex: "1000000",
      key: "1000000",
      render: (pricing: SubscriptionAgeTier['1000000']) =>
        `${pricing.regular.toFixed(2)}${pricing.promo ? ` (${pricing.promo.toFixed(2)} promo)` : ""}`,
    },
    {
      title: "10,000,000+ Pop.",
      dataIndex: "10000000",
      key: "10000000",
      render: (pricing: SubscriptionAgeTier['10000000']) =>
        `${pricing.regular.toFixed(2)}${pricing.promo ? ` (${pricing.promo.toFixed(2)} promo)` : ""}`,
    },
    {
      title: "Savings % (Promo)",
      dataIndex: "percentage",
      key: "percentage",
      render: (percentage: number | null) => (percentage ? `${percentage}%` : "-"),
    },
  ];

  return (
    <React.Fragment>
      <Helmet>
        <title>OLSME Subscription Pricing 2025 | Sun Light Meditation</title>
        <meta name="description" content="Explore OLSME subscription pricing for 2025 to join Sun Light Meditation programs. Start your journey at olsme.com/OLSStudentProspect!" />
        <meta name="keywords" content="OLSME subscription, Sun Light Meditation, 2025 pricing, OneLightSystem OLS, join OLS" />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLSME Subscription Pricing 2025 | Sun Light Meditation" />
        <meta property="og:description" content="View OLSME 2025 subscription pricing and join our Sun Light Meditation programs. Register at olsme.com/OLSStudentProspect!" />
        <meta property="og:url" content="https://www.olsme.com/olsmeSub" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.olsme.com/olsmeSub" />
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "OneLightSystem OLS",
              "url": "https://www.olsme.com",
              "subOrganization": {
                "@type": "EducationalOrganization",
                "name": "OLSME",
                "url": "https://www.olsme.com/olsmeSub",
                "description": "OLSME offers subscription pricing for Sun Light Meditation programs in 2025."
              }
            },
            {
              "@type": "Product",
              "name": "OLSME Subscription",
              "description": "Join OLSME to access Sun Light Meditation sessions with 2025 pricing plans.",
              "offers": {
                "@type": "Offer",
                "url": "https://www.olsme.com/olsmeSub",
                "priceCurrency": "USD",
                "price": "33.00",
                "availability": "https://schema.org/OnlineOnly"
              }
            }
          ])}
        </script>
      </Helmet>

      <SubscriptionPageWrapper>
        <Title level={2} className="section-title">OLSME Subscription Pricing 2025</Title>
        <Paragraph className="content-text">
          12-month OLSME subscription fees for 2025 (based on USA population size).
        </Paragraph>
        <SearchWrapper>
          <Search
            placeholder="Search by age range (e.g., 15-21)"
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
            aria-label="Search subscription pricing by age range"
          />
        </SearchWrapper>
        <SubscriptionTableStyled
          dataSource={filteredData}
          columns={columns}
          pagination={false}
          rowKey="ageDisplay"
        />
        <div className="mobile-table">
          {filteredData.map((tier) => (
            <SubscriptionCardStyled key={tier.ageDisplay}>
              <CardTitle level={4}>{tier.ageDisplay}</CardTitle>
              <Row gutter={[8, 8]}>
                {Object.entries(tier).map(([key, value]) => {
                  if (["ageRange", "ageDisplay", "percentage"].includes(key)) return null;
                  return (
                    <Col xs={12} key={key}>
                      <CardText>{key} Pop.: ${(value as any).regular.toFixed(2)}{(value as any).promo ? ` (${(value as any).promo.toFixed(2)} promo)` : ""}</CardText>
                    </Col>
                  );
                })}
                <Col xs={24}>
                  <CardText>Savings %: {tier.percentage ? `${tier.percentage}%` : "-"}</CardText>
                </Col>
              </Row>
            </SubscriptionCardStyled>
          ))}
        </div>
        <div className="description-section">
          <Paragraph className="content-text">
            OLSME for first-time users requires a 1-year subscription minimum to get started, then possible monthly terms.
          </Paragraph>
        </div>
      </SubscriptionPageWrapper>

      <CTASectionStyled>
        <Title level={2} className="section-title">Join OLSME Today!</Title>
        <Paragraph className="content-text">
          Start your Sun Light Meditation journey with OLSME’s subscription plans.
        </Paragraph>
        <Button className="custom-button" size="large" aria-label="Subscribe to OLSME">
          <Link to="/register">Subscribe Now</Link>
        </Button>
      </CTASectionStyled>
    </React.Fragment>
  );
};

export default OlsmeSub;